import {
	Bar,
	BarChart,
	CartesianGrid,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { ChartCard } from "./chart-card";
import { BAR_VALUE_LABEL, NO_ANIMATION } from "./chart-ticks";
import { COLORS } from "./utils";
import type { HocBaHistogramBin } from "@/lib/statistics-api.service";

interface HocBaHistogramChartProps {
	/** `undefined` = lần tải đầu chưa xong. */
	bins: HocBaHistogramBin[] | undefined;
	overallAverage: number | undefined;
	counted: number | undefined;
	skipped: number | undefined;
}

const TITLE = "Điểm học bạ tổ hợp (thang 30)";

/**
 * Histogram phân bố **điểm học bạ tổ hợp thang 30** — trung bình 3 năm lớp
 * 10/11/12 của 3 môn trong tổ hợp. KHÔNG PHẢI điểm đầu vào/trúng tuyển/xét
 * tuyển: công thức tuyển sinh thật còn 80% điểm THPT/ĐGNL mà cơ sở dữ liệu
 * này không có. Xem `HocBaScoreStats` trong `statistics-api.service.ts`.
 */
export function HocBaHistogramChart({
	bins,
	overallAverage,
	counted,
	skipped,
}: HocBaHistogramChartProps) {
	if (bins === undefined) {
		return (
			<ChartCard title={TITLE} filename="phan-bo-diem-hoc-ba-to-hop">
				<Skeleton className="h-[360px] w-full" />
			</ChartCard>
		);
	}

	if (bins.length === 0 || (counted ?? 0) === 0) {
		return (
			<ChartCard title={TITLE} filename="phan-bo-diem-hoc-ba-to-hop">
				<p className="py-8 text-center text-sm text-muted-foreground">
					Chưa có hồ sơ nào tính được điểm học bạ tổ hợp trong phạm vi đang lọc.
				</p>
			</ChartCard>
		);
	}

	// Một cột duy nhất cho mỗi bin: tập hồ sơ của cả tab đã là "đã tiếp nhận
	// hoặc đã đóng học phí", nên không còn nhóm nào để tách ra so sánh.
	const data = bins.map((bin) => ({
		range: `${bin.from}-${bin.to}`,
		"Số thí sinh": bin.total,
	}));

	const descriptionParts = [`${(counted ?? 0).toLocaleString("vi-VN")} hồ sơ tính được điểm`];
	// Chỉ nêu số bị bỏ qua khi có, để không làm rối phần lớn các lượt lọc
	// (khoá 2026) vốn không thiếu hồ sơ nào.
	if (skipped) {
		descriptionParts.push(
			`${skipped.toLocaleString("vi-VN")} hồ sơ thiếu dữ liệu, không tính được điểm`,
		);
	}

	return (
		<ChartCard
			title={TITLE}
			filename="phan-bo-diem-hoc-ba-to-hop"
			description={descriptionParts.join(" · ")}
		>
			<ResponsiveContainer width="100%" height={360}>
				<BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
					<CartesianGrid strokeDasharray="3 3" />
					<XAxis dataKey="range" tick={{ fontSize: 11 }} />
					<YAxis allowDecimals={false} />
					<Tooltip />
					<Bar {...NO_ANIMATION} dataKey="Số thí sinh" fill={COLORS[0]} label={BAR_VALUE_LABEL} />
					{overallAverage !== undefined && (
						<ReferenceLine
							x={`${Math.floor(overallAverage)}-${Math.floor(overallAverage) + 1}`}
							stroke="hsl(var(--foreground))"
							strokeDasharray="4 4"
							label={{
								value: `TB chung: ${overallAverage.toFixed(2)}`,
								position: "top",
								fontSize: 11,
								fill: "hsl(var(--foreground))",
							}}
						/>
					)}
				</BarChart>
			</ResponsiveContainer>
		</ChartCard>
	);
}
